import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Briefcase, MapPin, Clock, DollarSign } from "lucide-react";

const PLACEHOLDERS = [
  { id: "p1", title: "Steadicam Operator — 3-day feature", department: "Camera", location: "Kanab, UT", rate: "$1,200/day", type: "Feature" },
  { id: "p2", title: "Gaffer needed for indie doc", department: "G&E", location: "Zion Corridor", rate: "$650/day", type: "Documentary" },
  { id: "p3", title: "Colorist — spec commercial", department: "Post", location: "Remote / St. George", rate: "Project", type: "Commercial" },
  { id: "p4", title: "Location Sound Mixer", department: "Sound", location: "Moab, UT", rate: "$800/day", type: "Feature" },
  { id: "p5", title: "Production Assistant — 2 weeks", department: "Production", location: "Cedar City, UT", rate: "$275/day", type: "Feature" },
];

export default function Jobs() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dept, setDept] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await api.jobs();
        const arr = Array.isArray(data) && data.length ? data : PLACEHOLDERS;
        if (alive) setJobs(arr);
      } catch { if (alive) setJobs(PLACEHOLDERS); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  const departments = Array.from(new Set(jobs.map(j => j.department).filter(Boolean)));
  const filtered = dept ? jobs.filter(j => j.department === dept) : jobs;

  return (
    <div>
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.3em] text-neutral-400 mb-3">Jobs & Marketplace</div>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tighter leading-none font-medium">Working. Now.</h1>
        <p className="text-neutral-400 mt-3 max-w-xl">Roles, gear, and services from verified Film Front members.</p>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        <button
          onClick={() => setDept("")}
          className={`px-3 py-1.5 rounded-full text-xs uppercase tracking-widest transition-colors ${dept === "" ? "ff-accent-bg text-black" : "bg-white/5 text-neutral-300 hover:bg-white/10"}`}
          data-testid="jobs-filter-all"
        >
          All departments
        </button>
        {departments.map((d) => (
          <button
            key={d}
            onClick={() => setDept(d)}
            className={`px-3 py-1.5 rounded-full text-xs uppercase tracking-widest transition-colors ${dept === d ? "ff-accent-bg text-black" : "bg-white/5 text-neutral-300 hover:bg-white/10"}`}
          >
            {d}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-sm text-neutral-500">Loading jobs…</div>
      ) : filtered.length === 0 ? (
        <div className="ff-card p-10 text-center text-neutral-400">No jobs yet in this filter.</div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4 ff-stagger" data-testid="jobs-grid">
          {filtered.map((j, i) => (
            <div key={j.id || i} className="ff-card p-6 flex gap-4" data-testid={`job-card-${i}`}>
              <div className="w-11 h-11 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center flex-shrink-0">
                <Briefcase className="w-4 h-4 ff-accent-text" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs uppercase tracking-widest text-neutral-500">{j.department || j.type}</div>
                <div className="font-display text-lg tracking-tight mt-1 leading-snug">{j.title || j.role}</div>
                <div className="mt-2 flex flex-wrap gap-3 text-xs text-neutral-400">
                  {j.location && <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {j.location}</span>}
                  {j.rate && <span className="inline-flex items-center gap-1"><DollarSign className="w-3.5 h-3.5" /> {j.rate}</span>}
                  {(j.type && j.type !== j.department) && <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {j.type}</span>}
                </div>
                {j.description && <div className="mt-3 text-sm text-neutral-300 line-clamp-2">{j.description}</div>}
                <div className="mt-4">
                  <button className="ff-btn-ghost text-xs" data-testid={`job-apply-${i}`}>Apply via mobile app</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
